import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputBase, styled } from '@mui/material';
import { flowRight } from 'lodash';
import MaskedInput from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { gridShortDate, isValidDate, slashFormat, TEXT_MASK_DATE_FORMAT } from 'utilities';
import {
  DATE_SEGMENTS,
  getAdjacentSegmentIndex,
  getDateSegmentIndex,
  isDateSegmentComplete,
  selectDateSegment,
} from './gridDateSegmentNavigation';

interface TextMaskDateProps {
  inputRef: (ref: HTMLInputElement | null) => void;
  [key: string]: any;
}

// Styled components
const ValueViewer = styled('div')({
  cursor: 'pointer',
  width: '100%',
  height: '100%',
});

const ValueContainer = styled('div')({
  position: 'relative',
  height: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'end',
});

const StyledInputBase = styled(InputBase)<{ hasError: boolean }>(({ hasError }) => ({
  '& > input': {
    fontFamily: '"Open Sans", sans-serif, arial !important',
    padding: '6px 0',
    textAlign: 'right !important',
  },
  ...(hasError && {
    '& .MuiInputBase-input': {
      color: 'red',
      borderColor: 'red',
    },
  }),
}));

export const TextMaskDate: React.FC<TextMaskDateProps> = props => {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={(ref: any) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
      showMask
      guide
      keepCharPositions
      pipe={createAutoCorrectedDatePipe(TEXT_MASK_DATE_FORMAT, {
        minYear: 1900,
      })}
    />
  );
};

const resolveDateFromEditValue = (editValue: string | undefined, cellValue: unknown): string => {
  if (editValue !== undefined) {
    if (editValue === '') {
      return '';
    }
    return slashFormat(editValue as string);
  }
  if (cellValue) {
    return slashFormat(cellValue as string);
  }
  return '';
};

interface GridDateEditorProps {
  cell: ICell;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  value?: string;
  doubleClicked?: boolean;
  setDoubleClicked?: (value: boolean) => void;
}

const GridDateEditor = ({
  cell,
  onChange,
  onKeyDown,
  value,
  doubleClicked,
  setDoubleClicked,
}: GridDateEditorProps) => {
  const [error, setError] = useState<boolean>(false);
  const [date, setDate] = useState<string>(() => resolveDateFromEditValue(value, cell.value));
  const inputRef = useRef<HTMLInputElement>(null);
  const activeSegmentRef = useRef(0);
  const editEntryRef = useRef({ cellKey: '', initialDate: '' });

  if (cell.key !== editEntryRef.current.cellKey) {
    editEntryRef.current = {
      cellKey: cell.key ?? '',
      initialDate: resolveDateFromEditValue(value, cell.value),
    };
  }

  useEffect(() => {
    if (value !== undefined) {
      setDate(value === '' ? '' : slashFormat(value as string));
    }
  }, [value]);

  useEffect(() => {
    if (doubleClicked) {
      setDoubleClicked?.(false);
    }
  }, [doubleClicked, setDoubleClicked]);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }
    inputRef.current.focus();
    activeSegmentRef.current = 0;
    selectDateSegment(inputRef.current, 0);
  }, [cell.key]);

  useEffect(() => {
    if (date && isValidDate(date)) {
      setError(false);
      onChange(gridShortDate(date));
    } else {
      setError(true);
    }
  }, [date, onChange]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const dateParam = event.target.value;
    const editingSegment = activeSegmentRef.current;
    setDate(dateParam);

    requestAnimationFrame(() => {
      if (!inputRef.current) {
        return;
      }
      if (
        isDateSegmentComplete(dateParam, editingSegment)
        && editingSegment < DATE_SEGMENTS.length - 1
      ) {
        const nextSegment = editingSegment + 1;
        activeSegmentRef.current = nextSegment;
        selectDateSegment(inputRef.current, nextSegment);
      }
    });
  }, []);

  const handleFocus = useCallback(() => {
    if (!inputRef.current) {
      return;
    }
    const segmentIndex = date ? getDateSegmentIndex(inputRef.current.selectionStart ?? 0) : 0;
    activeSegmentRef.current = segmentIndex;
    selectDateSegment(inputRef.current, segmentIndex);
  }, [date]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const segmentIndex = getDateSegmentIndex(input.selectionStart ?? 0);
    activeSegmentRef.current = segmentIndex;
    selectDateSegment(input, segmentIndex);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        event.stopPropagation();
        if (inputRef.current) {
          const current = getDateSegmentIndex(inputRef.current.selectionStart ?? 0);
          const prev = getAdjacentSegmentIndex(current, 'prev');
          activeSegmentRef.current = prev;
          selectDateSegment(inputRef.current, prev);
        }
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        if (inputRef.current) {
          const current = getDateSegmentIndex(inputRef.current.selectionStart ?? 0);
          const next = getAdjacentSegmentIndex(current, 'next');
          activeSegmentRef.current = next;
          selectDateSegment(inputRef.current, next);
        }
        return;
      }
      onKeyDown?.(event);
    },
    [onKeyDown]
  );

  const handleInputRef = useCallback((ref: HTMLInputElement | null) => {
    if (inputRef.current !== ref) {
      (inputRef as any).current = ref;
    }
  }, []);

  if (cell.hidden) {
    return null;
  }

  return (
    <ValueViewer>
      <ValueContainer>
        <StyledInputBase
          id={`${cell.key}-date_editor`}
          value={date || ''}
          inputComponent={TextMaskDate as any}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          hasError={error}
          inputProps={{
            inputRef: handleInputRef,
            onClick: handleClick,
          }}
        />
      </ValueContainer>
    </ValueViewer>
  );
};

const enhance = flowRight(withTooltip, withRequired);

export default enhance(GridDateEditor);
